import React, { FC, useContext } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { LineItemInvalidEnumStr, LineItemInvalidEnum } from 'enums'
import { PrivateCartType, ProductCartType, PrivateCartAddItemType } from 'types/cart'
import { OrderLineItemType } from 'services/hooks/dine-in/useGetGlobalCart'
import CartItem from 'components/dine-in/CartItem'
import InvalidList from 'components/dine-in/InvalidList'
import { AppContext } from 'contexts/AppProvider'

interface CartItemListProps {
  currentPrivateCart: PrivateCartType | PrivateCartAddItemType
  onUpdateItemQuantity: (quantity: number, index: number) => void
  onClickEdit: (index: number) => void
  onClickDelete: (index: number) => void
  onClickProduct?: (product: ProductCartType) => void
  lineItemsResult: OrderLineItemType[]
  hideZeroPrice?: boolean
  productRemovingIndex: number
}

const CartItemList: FC<CartItemListProps> = ({
  currentPrivateCart,
  onUpdateItemQuantity,
  onClickEdit,
  onClickDelete,
  onClickProduct = () => {},
  lineItemsResult,
  hideZeroPrice = false,
  productRemovingIndex,
}) => {
  const { isThaiLang } = useContext(AppContext)

  const getProductCartItem = (product: ProductCartType) => {
    const modifiers = product.modifiers.map(({ nameTh, nameEn, priceSatangs, quantity, multipleQuantity }) => ({
      name: isThaiLang ? nameTh : nameEn,
      priceSatangs,
      quantity,
      multipleQuantity,
    }))

    return {
      images: product.item.images,
      name: isThaiLang ? product.item.nameTh : product.item.nameEn,
      modifiers,
      totalPriceSatangs: product.item.totalPriceSatangs,
      quantity: product.quantity,
      excludedFromPromotion: product.item.excludedFromPromotion,
      customNotes: product.customNotes,
    }
  }

  const getInvalidsLineItem = (lineItem: ProductCartType) => {
    const lineItemInvalids: LineItemInvalidEnumStr[] = []
    lineItemsResult.forEach(
      (product) =>
        product.productID === lineItem.item.id && product.invalids?.forEach((invalid) => lineItemInvalids.push(invalid))
    )

    return lineItemInvalids
  }

  return (
    <>
      {currentPrivateCart?.products.map((product, index) => {
        const { privateCartIndex } = product
        const editIndex = privateCartIndex ?? index
        const productName = isThaiLang ? product.item.nameTh : product.item.nameEn
        const lineItemInvalids = getInvalidsLineItem(product)
        const productCartItem = getProductCartItem(product)

        const isInactiveItem = lineItemInvalids.includes(LineItemInvalidEnum.Inactive)
        const isMembershipRequiredItem = lineItemInvalids.includes(LineItemInvalidEnum.MembershipRequired)
        const isTierIneligibleItem = lineItemInvalids.includes(LineItemInvalidEnum.TierIneligible)
        const isTierBirthMonthRequiredItem = lineItemInvalids.includes(LineItemInvalidEnum.TierBirthMonthRequired)
        const isGeneralInvalid =
          lineItemInvalids.length > 0 &&
          !isInactiveItem &&
          !isMembershipRequiredItem &&
          !isTierIneligibleItem &&
          !isTierBirthMonthRequiredItem

        return (
          <Wrapper
            key={index}
            className="private-cart-item-list"
            data-testid="private-cart-item-list"
            isRemoving={productRemovingIndex === index}
          >
            <CartItem
              product={productCartItem}
              onUpdateQuantity={(quantity) => onUpdateItemQuantity(quantity, editIndex)}
              onClickEdit={() => onClickEdit(editIndex)}
              onClickDelete={() => onClickDelete(editIndex)}
              onClickItem={() => onClickProduct(product)}
              isInactive={isGeneralInvalid || isInactiveItem}
              hideZeroPrice={hideZeroPrice}
            />
            <InvalidList
              isNotFound={isInactiveItem}
              productName={productName}
              listModifierIsNotFound={[]}
              isMembershipRequiredItem={isMembershipRequiredItem}
              isTierIneligibleItem={isTierIneligibleItem}
              isTierBirthMonthRequiredItem={isTierBirthMonthRequiredItem}
              isModifierNotFound={false}
              isGeneralInvalid={isGeneralInvalid}
            />
          </Wrapper>
        )
      })}
    </>
  )
}

export default CartItemList

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
`

const Wrapper = styled.div<{ isRemoving: boolean }>`
  animation: ${fadeIn} 2s ease-in;
  transition: opacity 0.3s ease-in;
  ${({ isRemoving }) =>
    isRemoving &&
    css`
      opacity: 0;
    `}
`
